import { AuthContext } from '@/contexts';
import { SHORTENED_ERROR_MESSAGES } from '@/enums';
import { useGetTenantQuery } from '@/hooks/useGetTenantQuery';
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from '@/routes';
import { AuthService } from '@/services';
import { CustomError, errorNotification } from '@/utils';
import { OtpSchema } from '@/validations';
import { ControlledInput, CustomButton } from '@amili-v3/amili-v3-design';
import { Col, Form, Input, InputRef, notification } from 'antd';
import { AxiosResponse } from 'axios';
import { Formik } from 'formik';
import {
  ChangeEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MutationState } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import AuthCardHeader from '../AuthCardHeader';
import { AuthHelperText, AuthModal } from '../styles';
import { CaptchaInputWrapper, CaptchaWrapper, OtpRow } from './styles';

const FORM_ID = 'otp form';
const LIMIT_WRONG_CODE = 3;
export const LIMIT_WRONG_CAPTCHA = 6;

interface IChangeEvent {
  e: ChangeEvent<HTMLInputElement>;
  index: number;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
}

interface IKeyDownEvent extends Omit<IChangeEvent, 'e'> {
  e: KeyboardEvent<HTMLInputElement>;
}

type LoginMutationState = MutationState<AxiosResponse, unknown, any, unknown>;

const OTP = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setAuth } = useContext(AuthContext);

  const { tenant } = useGetTenantQuery();

  const { appClientId: clientId } = tenant;

  const ref = useRef<InputRef>(null);
  const [open, setOpen] = useState(true);
  const [otp, setOtp] = useState<string[]>([]);
  const [otpIndex, setOtpIndex] = useState(0);

  const loginCache = queryClient
    .getMutationCache()
    .findAll({ mutationKey: ['signIn'] })
    .sort((a, b) => b.mutationId - a.mutationId)?.[0]
    ?.state as LoginMutationState;

  const { data, variables } = loginCache ?? {};
  const { session, trackingOtp } = data?.data ?? {};
  const { email } = variables ?? {};
  const { count: resCount } = trackingOtp ?? {};

  const [count, setCount] = useState(resCount ?? 0);

  const [captcha, setCaptcha] = useState<{ img: string; captchaId: string }>(
    {} as { img: string; captchaId: string },
  );

  const handleError = (errorCode: string, errorMessage: string) => {
    if (
      errorMessage?.includes('expired') ||
      errorMessage?.includes('Invalid session')
    ) {
      notification.error({ message: t('authentication.sessionExpired') });
      return navigate(PUBLIC_ROUTES.SIGN_IN);
    }
    if (errorCode !== SHORTENED_ERROR_MESSAGES.INVALID_CAPTCHA) {
      setOtp([]);
      setOtpIndex(0);
    }
    setCaptcha({ img: captcha.img, captchaId: '' });
    if (count >= LIMIT_WRONG_CAPTCHA - 1) {
      notification.error({
        message: t('authentication.wrongCodeManyTime', {
          minute: 5,
          tail: 's',
        }),
      });
      navigate(PUBLIC_ROUTES.SIGN_IN);
    } else {
      notification.error({ message: t('error.' + errorCode) });
    }
  };

  const handleChange = (event: IChangeEvent) => {
    const { e, index, setFieldValue } = event;
    const { value } = e.target;
    const { nativeEvent } = e as any;
    if (!value) return;
    if (nativeEvent?.inputType === 'insertFromPaste') {
      const newValue = value.split('');
      let newOtp = [...otp];
      newOtp.splice(index, newValue.length, ...newValue);
      newOtp = newOtp.slice(0, 6);
      setFieldValue('otp', newOtp.join(''));
      setOtp(newOtp);
      setOtpIndex(Math.min(5, index + newValue.length));
      return;
    }
    const newValue = value.split('');
    const find = newValue.find(item => item !== otp[index]);
    const newOtp = [...otp];
    newOtp[index] = find || newValue[0];
    setFieldValue('otp', newOtp.join(''));
    setOtp(newOtp);
    setOtpIndex(Math.min(5, index + 1));
  };

  const handleKeyDown = (event: IKeyDownEvent) => {
    const { e, index, setFieldValue } = event;
    const { key } = e;
    if (key === 'Backspace') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      setOtpIndex(Math.max(0, index - 1));
      setFieldValue('otp', newOtp.join(''));
    } else if (key === 'ArrowRight') {
      setOtpIndex(Math.min(5, index + 1));
    } else if (key === 'ArrowLeft') {
      setOtpIndex(Math.max(0, index - 1));
    }
  };

  const mutationSendOtp = useMutation({
    mutationFn: AuthService.verifyOtp,
    onSuccess: res => {
      const {
        error,
        authenticationResult,
        trackingCountOtp = { count: 0 },
      } = res.data as any;
      setCount(trackingCountOtp.count);
      if (error) {
        return handleError(error.errorCode, error.message);
      }
      const { accessToken, idToken, refreshToken } = authenticationResult;
      setAuth({ idToken, accessToken, session, refreshToken });
      notification.success({ message: t('message.signInSuccess') });
      navigate(PRIVATE_ROUTES.APP);
    },
  });

  const mutationGetCaptcha = useMutation({
    mutationFn: AuthService.getCaptcha,
    onSuccess: res => {
      const { captchaId, data } = res.data;
      setCaptcha({ captchaId, img: data });
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const handleSubmit = (value: { otp: string; captcha?: string }) => {
    const { otp: codeOtp, captcha: verifyValue = undefined } = value;
    const payload: any = {
      codeOtp,
      clientId: clientId,
      email,
      session,
    };
    if (verifyValue) {
      payload.verifyValue = verifyValue;
      payload.id = captcha.captchaId;
    }
    return mutationSendOtp.mutateAsync(payload);
  };

  useEffect(() => {
    if (
      count >= LIMIT_WRONG_CODE &&
      !captcha.captchaId &&
      !mutationGetCaptcha.isPending
    ) {
      mutationGetCaptcha.mutateAsync();
    }
  }, [count, mutationGetCaptcha, captcha]);

  useEffect(() => {
    ref.current?.focus();
  }, [otpIndex, ref]);

  const initialValues = useMemo(
    () => ({
      otp: '',
      captcha: '',
      showCaptcha: count >= LIMIT_WRONG_CODE,
    }),
    [count],
  );

  return (
    <AuthModal
      centered
      open={open}
      mask={false}
      width={420}
      layout="vertical"
      footerBorder={false}
      footer={[
        <CustomButton
          key="submit"
          size="large"
          width="100%"
          form={FORM_ID}
          htmlType="submit"
          aria-label="Submit btn"
          loading={mutationSendOtp.isPending}
          disabled={mutationGetCaptcha.isPending}
        >
          {t('common.submit')}
        </CustomButton>,
      ]}
      hideCloseButton
      title={<AuthCardHeader heading={t('authentication.otp')} />}
      goBack={() => {
        setOpen(false);
        setTimeout(() => {
          navigate(PUBLIC_ROUTES.SIGN_IN);
        }, 200);
      }}
    >
      <AuthHelperText>{t('authentication.otpHelper')}</AuthHelperText>
      <Formik
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={OtpSchema}
        initialValues={initialValues}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          handleSubmit: handleFinish,
        }) => {
          if (otp.length === 0 && otp.join('') !== values.otp) {
            setFieldValue('otp', '');
            setFieldTouched('otp', false);
          }
          if (captcha.captchaId === '' && values.captcha !== '') {
            setFieldValue('captcha', '');
            setFieldTouched('captcha', false);
          }
          return (
            <Form id={FORM_ID} layout="vertical" onFinish={handleFinish}>
              <OtpRow gutter={[20, 10]}>
                {new Array(6).fill(null).map((_, index) => (
                  <Col xs={4} key={index}>
                    <Input
                      type="number"
                      value={otp[index]}
                      aria-label="otp input"
                      ref={otpIndex === index ? ref : null}
                      onChange={e => handleChange({ e, index, setFieldValue })}
                      onKeyDown={e =>
                        handleKeyDown({ e, index, setFieldValue })
                      }
                    />
                  </Col>
                ))}
              </OtpRow>

              <ControlledInput name="otp" hideInput />

              {count >= LIMIT_WRONG_CODE && captcha.img && (
                <CaptchaWrapper>
                  <img src={captcha.img} alt="captcha" />
                  <CaptchaInputWrapper>
                    <ControlledInput
                      maxLength={6}
                      name="captcha"
                      placeholder={t('authentication.enterCaptcha') as string}
                    />
                  </CaptchaInputWrapper>
                </CaptchaWrapper>
              )}
            </Form>
          );
        }}
      </Formik>
    </AuthModal>
  );
};

export default OTP;
